/**
 * System Forms - Common (shared across all clinic types)
 *
 * These are read-only, hard-coded forms available to all therapists.
 * Users can duplicate them to create editable cloud copies.
 *
 * TAG REFERENCE (from DEVELOPER_GUIDE.md Section 4.14.1):
 *   Patient field:    <span class="patient-field-placeholder" data-field="FIELD_KEY" contenteditable="false">{LABEL}</span>
 *   Free text:        <span class="free-text-placeholder" data-label="LABEL" contenteditable="false">[טקסט חופשי: LABEL]</span>
 *   Client signature: <div class="signature-placeholder" contenteditable="false"><div class="signature-line"></div><span class="signature-label">חתימת לקוח</span></div>
 *   Signature date:   <span class="signature-date-placeholder" contenteditable="false">[תאריך חתימה]</span>
 */

import { ALL_CLINIC_TYPE_CODES } from "../../clinicTypesConfig";
const ALL_CLINIC_TYPES = ALL_CLINIC_TYPE_CODES;

const tag = (field, label) =>
  `<span class="patient-field-placeholder" data-field="${field}" contenteditable="false">{${label}}</span>`;

const clientSignature =
  `<div class="signature-placeholder" contenteditable="false"><div class="signature-line"></div><span class="signature-label">חתימת לקוח</span></div>`;

const signatureDate =
  `<span class="signature-date-placeholder" contenteditable="false">[תאריך חתימה]</span>`;

export const commonForms = [
  {
    id: "sys_form_confidentiality_waiver",
    name: "טופס ויתור סודיות",
    content:
      `<h2 style="text-align:center;">טופס ויתור סודיות</h2>` +
      `<p style="text-align:center;">${tag("clinicName", "שם הקליניקה")}</p>` +
      `<hr/>` +

      `<p>אני, ${tag("fullName", "שם מלא")}, נושא/ת ת.ז. ${tag("idNumber", "תעודת זהות")},</p>` +

      `<p>מאשר/ת בזאת כי ידוע לי שכל המידע הנמסר במסגרת הטיפול, לרבות פרטים אישיים, תוכן השיחות ומהלך הטיפול, הינו חסוי בהתאם לחוק זכויות החולה, התשנ"ו-1996 ולכללי האתיקה המקצועית.</p>` +

      `<h3>הסכמה להעברת מידע</h3>` +
      `<p>אני מאשר/ת בזאת את העברת מידע טיפולי הנוגע אליי, לרבות סיכומי טיפול, אבחונים, תוכניות טיפול והמלצות מקצועיות, לגורמים המטפלים הרלוונטיים — ובכלל זה רופאים, מטפלים ואנשי מקצוע נוספים בתחום הבריאות, מוסדות חינוך ורווחה, וכן חברות ביטוח ומוסדות ציבוריים — ככל שהדבר נדרש לצורך המשך הטיפול, ייעוץ מקצועי, הפניה או כל מטרה טיפולית אחרת.</p>` +
      `<hr/>` +

      `<h3>תנאים</h3>` +
      `<ul>` +
      `<li>ויתור זה תקף למשך תקופת הטיפול בלבד, אלא אם צוין אחרת.</li>` +
      `<li>ידוע לי שבאפשרותי לבטל הסכמה זו בכל עת בהודעה בכתב למטפל/ת.</li>` +
      `<li>המידע יועבר רק לגורמים שצוינו לעיל ולמטרה המוגדרת בלבד.</li>` +
      `<li>ביטול ההסכמה לא יחול על מידע שכבר הועבר בהתאם לטופס זה.</li>` +
      `</ul>` +
      `<hr/>` +

      `<h3>הצהרה</h3>` +
      `<p>אני מצהיר/ה כי קראתי והבנתי את תוכן טופס זה, וכי הסכמתי ניתנת מרצון חופשי ובהבנה מלאה של המשמעויות.</p>` +
      `<br/>` +

      `<p><strong>שם המטופל/ת:</strong> ${tag("fullName", "שם מלא")}</p>` +
      `<p><strong>ת.ז.:</strong> ${tag("idNumber", "תעודת זהות")}</p>` +
      `<br/>` +

      `<p><strong>חתימה:</strong></p>` +
      `<p>${clientSignature}</p>` +
      `<p><strong>תאריך:</strong> ${signatureDate}</p>`,
    isSystem: true,
    clinicTypes: ALL_CLINIC_TYPES,
    category: "legal",
  },
  {
    id: "sys_form_informed_consent",
    name: "טופס הסכמה מדעת לטיפול",
    content:
      `<h2 style="text-align:center;">הסכמה מדעת לטיפול</h2>` +
      `<p style="text-align:center;">${tag("clinicName", "שם הקליניקה")}</p>` +
      `<hr/>` +
      `<p>אני, ${tag("fullName", "שם מלא")}, נושא/ת ת.ז. ${tag("idNumber", "תעודת זהות")}, מאשר/ת כי קיבלתי הסבר על מהות הטיפול המוצע, מטרותיו, אופן ביצועו, משך הזמן המשוער, התועלת הצפויה והסיכונים או אי-הנוחות האפשריים, וכן על חלופות טיפוליות ככל שישנן.</p>` +
      `<h3>הסכמתי</h3>` +
      `<ul>` +
      `<li>ניתנה לי הזדמנות לשאול שאלות וקיבלתי תשובות מספקות.</li>` +
      `<li>ידוע לי כי אני רשאי/ת להפסיק את הטיפול בכל עת, ללא צורך בנימוק.</li>` +
      `<li>ידוע לי כי המידע הטיפולי נשמר בסודיות בהתאם לחוק, למעט מקרים המחייבים דיווח על פי דין.</li>` +
      `<li>אני מתחייב/ת למסור מידע רפואי ואישי מלא ומדויק הרלוונטי לטיפול.</li>` +
      `</ul>` +
      `<hr/>` +
      `<h3>הצהרה</h3>` +
      `<p>אני מצהיר/ה כי קראתי והבנתי את האמור לעיל, וכי הסכמתי לטיפול ניתנת מרצוני החופשי.</p>` +
      `<br/>` +
      `<p><strong>שם המטופל/ת:</strong> ${tag("fullName", "שם מלא")}</p>` +
      `<p><strong>טלפון:</strong> ${tag("phone", "טלפון")}</p>` +
      `<br/>` +
      `<p><strong>חתימה:</strong></p>` +
      clientSignature +
      `<p><strong>תאריך:</strong> ${signatureDate}</p>`,
    isSystem: true,
    clinicTypes: ALL_CLINIC_TYPES,
  },
  {
    id: "sys_form_minor_guardian_consent",
    name: "הסכמת הורה / אפוטרופוס לטיפול בקטין/ה",
    content:
      `<h2 style="text-align:center;">הסכמת הורה / אפוטרופוס לטיפול בקטין/ה</h2>` +
      `<p style="text-align:center;">${tag("clinicName", "שם הקליניקה")}</p>` +
      `<hr/>` +
      `<p><strong>פרטי הקטין/ה:</strong> ${tag("fullName", "שם מלא")}, ת.ז. ${tag("idNumber", "תעודת זהות")}, תאריך לידה ${tag("birthDate", "תאריך לידה")}</p>` +
      `<p><strong>הורה / אפוטרופוס 1:</strong> ${tag("parent1Name", "הורה 1 - שם")}, ת.ז. ${tag("parent1Id", "הורה 1 - תעודת זהות")}, טלפון ${tag("parent1Phone", "הורה 1 - טלפון")}</p>` +
      `<p><strong>הורה / אפוטרופוס 2:</strong> ${tag("parent2Name", "הורה 2 - שם")}, ת.ז. ${tag("parent2Id", "הורה 2 - תעודת זהות")}, טלפון ${tag("parent2Phone", "הורה 2 - טלפון")}</p>` +
      `<hr/>` +
      `<h3>הסכמה</h3>` +
      `<p>אנו, ההורים / האפוטרופוסים החוקיים של הקטין/ה הנ"ל, מאשרים בזאת את קבלת הטיפול בקליניקה, לאחר שקיבלנו הסבר על מהותו, מטרותיו ואופן ביצועו.</p>` +
      `<ul>` +
      `<li>אנו מצהירים כי אנו מוסמכים לתת הסכמה זו, וכי לא קיימת מניעה חוקית לטיפול.</li>` +
      `<li>ככל שההורים גרושים או פרודים — אנו מצהירים כי ההסכמה ניתנת על דעת שני ההורים, אלא אם קיימת החלטה שיפוטית הקובעת אחרת.</li>` +
      `<li>אנו מתחייבים לעדכן את המטפל/ת בכל שינוי במצבו/ה הבריאותי של הקטין/ה או בפרטי ההתקשרות.</li>` +
      `<li>ידוע לנו כי ניתן להפסיק את הטיפול בכל עת בהודעה למטפל/ת.</li>` +
      `</ul>` +
      `<hr/>` +
      `<p><strong>הערות / מידע רפואי רלוונטי:</strong> <span class="free-text-placeholder" data-label="מידע רפואי רלוונטי" contenteditable="false">[טקסט חופשי: מידע רפואי רלוונטי]</span></p>` +
      `<br/>` +
      `<p><strong>חתימת הורה / אפוטרופוס 1:</strong></p>` +
      clientSignature +
      `<p><strong>חתימת הורה / אפוטרופוס 2:</strong></p>` +
      `<div class="signature-placeholder" data-signature-id="2" contenteditable="false"><div class="signature-line"></div><span class="signature-label">חתימה 2</span></div>` +
      `<p><strong>תאריך:</strong> ${signatureDate}</p>`,
    isSystem: true,
    clinicTypes: ALL_CLINIC_TYPES,
  },
  {
    id: "sys_form_cancellation_policy",
    name: "מדיניות ביטולים ותשלומים",
    content:
      `<h2 style="text-align:center;">מדיניות ביטולים ותשלומים</h2>` +
      `<p style="text-align:center;">${tag("clinicName", "שם הקליניקה")}</p>` +
      `<hr/>` +
      `<p>אני, ${tag("fullName", "שם מלא")}, ת.ז. ${tag("idNumber", "תעודת זהות")}, מאשר/ת כי קראתי והבנתי את מדיניות הקליניקה כמפורט להלן:</p>` +
      `<h3>ביטול ושינוי מפגשים</h3>` +
      `<ul>` +
      `<li>ביטול או שינוי מועד מפגש ייעשה בהודעה מוקדמת של <strong>24 שעות לפחות</strong>.</li>` +
      `<li>מפגש שבוטל בהתראה קצרה מ-24 שעות, או שלא התקיים ללא הודעה, יחויב במלואו.</li>` +
      `<li>איחור למפגש לא יאריך את משך המפגש, והמפגש יחויב במלואו.</li>` +
      `<li>במקרה של ביטול מצד הקליניקה — המפגש לא יחויב וייקבע מועד חלופי.</li>` +
      `</ul>` +
      `<h3>תשלומים</h3>` +
      `<ul>` +
      `<li>התשלום עבור המפגש יתבצע בסיום כל מפגש, אלא אם סוכם אחרת בכתב.</li>` +
      `<li>עלות מפגש: <span class="free-text-placeholder" data-label="עלות מפגש" contenteditable="false">[טקסט חופשי: עלות מפגש]</span></li>` +
      `<li>הקליניקה מנפיקה קבלה / חשבונית עבור כל תשלום.</li>` +
      `<li>החזר מגורם מבטח (קופת חולים / ביטוח פרטי) הוא באחריות המטופל/ת מול הגורם המבטח.</li>` +
      `</ul>` +
      `<hr/>` +
      `<p>אני מאשר/ת כי הבנתי את המדיניות ומסכים/ה לתנאיה.</p>` +
      `<br/>` +
      `<p><strong>חתימה:</strong></p>` +
      clientSignature +
      `<p><strong>תאריך:</strong> ${signatureDate}</p>`,
    isSystem: true,
    clinicTypes: ALL_CLINIC_TYPES,
  },
  {
    id: "sys_form_online_therapy_consent",
    name: "הסכמה לטיפול מקוון (וידאו / טלפון)",
    content:
      `<h2 style="text-align:center;">הסכמה לטיפול מקוון</h2>` +
      `<p style="text-align:center;">${tag("clinicName", "שם הקליניקה")}</p>` +
      `<hr/>` +
      `<p>אני, ${tag("fullName", "שם מלא")}, ת.ז. ${tag("idNumber", "תעודת זהות")}, מסכים/ה לקבל טיפול באמצעות שיחת וידאו או טלפון, ומאשר/ת כי הוסברו לי הנקודות הבאות:</p>` +
      `<ul>` +
      `<li>טיפול מקוון עשוי להיות שונה מטיפול פנים אל פנים, וייתכנו מגבלות טכנולוגיות (איכות תקשורת, ניתוקים).</li>` +
      `<li>באחריותי לדאוג לסביבה פרטית ושקטה בזמן המפגש ולחיבור אינטרנט תקין.</li>` +
      `<li>המפגשים אינם מוקלטים על ידי הקליניקה, ואני מתחייב/ת שלא להקליטם ללא הסכמה מפורשת.</li>` +
      `<li>במצב חירום עליי לפנות לגורמי החירום (100 / 101) — הטיפול המקוון אינו מהווה תחליף לשירותי חירום.</li>` +
      `<li>מדיניות הביטולים והתשלומים של הקליניקה חלה גם על מפגשים מקוונים.</li>` +
      `</ul>` +
      `<p><strong>כתובת מיקומי בזמן המפגשים (לצורכי חירום):</strong> <span class="free-text-placeholder" data-label="כתובת בזמן המפגשים" contenteditable="false">[טקסט חופשי: כתובת בזמן המפגשים]</span></p>` +
      `<p><strong>איש קשר לחירום:</strong> ${tag("emergencyContactName", "איש קשר חירום - שם")}, ${tag("emergencyContactPhone", "איש קשר חירום - טלפון")}</p>` +
      `<hr/>` +
      `<p>אני מצהיר/ה כי קראתי, הבנתי ואני מסכים/ה לתנאים לעיל.</p>` +
      `<br/>` +
      `<p><strong>חתימה:</strong></p>` +
      clientSignature +
      `<p><strong>תאריך:</strong> ${signatureDate}</p>`,
    isSystem: true,
    clinicTypes: ALL_CLINIC_TYPES,
  },
];
